<script setup>
/**
 * The "linked ticket" activity, as a card instead of a grey pill.
 *
 * A ticket is a conversation like any other, so the card reads it from the
 * shared conversations store when it is already loaded and falls back to the
 * conversation endpoint once when it is not. Nothing new is stored: the card
 * keeps its own copy so the chat inbox list is not polluted with a ticket.
 * If the read fails, the card still names the ticket and links to it.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import ConversationApi from 'dashboard/api/inbox/conversation';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { presentTicket } from 'dashboard/helper/conversationRowPresenter';
import { dynamicTime } from 'shared/helpers/timeHelper';

import PriorityMark from 'dashboard/components-next/Conversation/PriorityMark.vue';
import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';

import { useMessageContext } from '../provider.js';

const props = defineProps({
  ticketId: { type: Number, required: true },
});

const { t } = useI18n();
const router = useRouter();
const { createdAt } = useMessageContext();
const { getPlainText } = useMessageFormatter();
const getConversationById = useMapGetter('getConversationById');

const fetchedTicket = ref(null);

const conversation = computed(
  () => getConversationById.value(props.ticketId) || fetchedTicket.value
);

const ticket = computed(() =>
  conversation.value ? presentTicket(conversation.value, getPlainText) : null
);

const readableTime = computed(() => dynamicTime(createdAt.value));

const loadTicket = async ticketId => {
  fetchedTicket.value = null;
  if (getConversationById.value(ticketId)) return;

  try {
    const { data } = await ConversationApi.show(ticketId);
    fetchedTicket.value = data;
  } catch (error) {
    // The card degrades to the ticket number and the link to it.
    fetchedTicket.value = null;
  }
};

watch(() => props.ticketId, loadTicket, { immediate: true });

const openTicket = () =>
  router.push({
    name: 'tickets_conversation',
    params: { conversationId: props.ticketId },
  });
</script>

<template>
  <div
    class="flex items-start w-96 max-w-full gap-3 px-3.5 py-3 border rounded-2xl border-n-weak bg-n-solid-1"
    data-bubble-name="ticket-activity"
  >
    <span class="mt-0.5 shrink-0 i-lucide-link size-4 text-n-slate-11" />
    <div class="flex flex-col min-w-0 gap-1 grow">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION.TICKET_ACTIVITY.TITLE', { id: ticketId }) }}
      </span>
      <span v-if="ticket" class="text-sm truncate text-n-slate-11">
        {{ ticket.subject || t('TICKETS.EMPTY.NO_SUBJECT') }}
      </span>
      <div
        class="flex flex-wrap items-center text-xs gap-x-2 gap-y-1 text-n-slate-10"
      >
        <PriorityMark
          v-if="ticket?.priority"
          :priority="ticket.priority"
          show-label
        />
        <StatusPill v-if="ticket" :state="ticket.state" />
        <span v-if="ticket" class="truncate">
          {{ ticket.assignee?.name || t('TICKETS.INFO.UNASSIGNED') }}
        </span>
        <span>{{ readableTime }}</span>
      </div>
    </div>
    <button
      type="button"
      :title="t('CONVERSATION.TICKET_ACTIVITY.OPEN')"
      :aria-label="t('CONVERSATION.TICKET_ACTIVITY.OPEN')"
      data-testid="open-linked-ticket"
      class="grid rounded-lg shrink-0 size-7 place-items-center text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="openTicket"
    >
      <span class="i-lucide-ticket size-4" />
    </button>
  </div>
</template>
